import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, Table, Button, Badge, LoadingSkeleton } from './ui';
import { ArrowLeftIcon, ArrowDownTrayIcon, ArrowUpTrayIcon } from '@heroicons/react/24/outline';
import { safeErrorMessage } from '../utils/apiError';
import useApi from './portal/useApi';
import { formatDate } from './portal/format';
import portal from './portal/Portal.module.css';
import styles from './StudentImport.module.css';

const NETWORK_ERROR = 'Network error. Please check your connection and try again.';
const LAYOUT_TEXT = {
  name: { full: 'one Full Name column', split: 'First Name + Surname columns' },
  class: { single: 'one Class column', split: 'Form + Class columns', none: 'no class column' },
};

function saveBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

const csvCell = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;

// Onboard many students from an Excel file: explains the format, checks the
// file, then imports the valid rows and reports the rejected ones with why.
function StudentImport({ token }) {
  const guide = useApi(token, '/api/v1/students/import/guide');
  const [file, setFile] = useState(null);
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');

  const downloadTemplate = async () => {
    setError('');
    try {
      const response = await fetch('/api/v1/students/import/template', { headers: { Authorization: `Bearer ${token}` } });
      if (response.ok) saveBlob(await response.blob(), 'student-import-template.xlsx');
      else setError(await safeErrorMessage(response, 'Failed to download the template'));
    } catch {
      setError(NETWORK_ERROR);
    }
  };

  const submit = async (dryRun) => {
    if (!file) {
      setError('Choose an Excel file first.');
      return;
    }
    setBusy(dryRun ? 'check' : 'import');
    setError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await fetch(`/api/v1/students/import?dry_run=${dryRun}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (response.ok) {
        setReport(await response.json());
      } else {
        setReport(null);
        setError(await safeErrorMessage(response, 'The file could not be processed'));
      }
    } catch {
      setError(NETWORK_ERROR);
    } finally {
      setBusy('');
    }
  };

  const downloadRejected = () => {
    const lines = [['Row', 'Name', 'Class', 'Why it was rejected'].map(csvCell).join(',')];
    report.rejected.forEach((row) => lines.push([row.row, row.full_name, row.class, row.reasons.join('; ')].map(csvCell).join(',')));
    saveBlob(new Blob([lines.join('\r\n')], { type: 'text/csv' }), 'rejected-students.csv');
  };

  const chooseFile = (e) => {
    setFile(e.target.files[0] || null);
    setReport(null);
    setError('');
  };

  const imported = report && !report.dry_run;
  const studentColumns = [
    { header: 'Row', accessor: 'row', width: '4rem' },
    { header: 'Name', render: (row) => <strong>{row.full_name || '—'}</strong> },
    { header: 'Class', render: (row) => row.class || '—' },
  ];

  return (
    <div>
      <Link to="/students" className={portal.backLink}>
        <ArrowLeftIcon style={{ width: 14, height: 14 }} />
        Students
      </Link>
      <div className={portal.header}>
        <div>
          <h2 className={portal.title}>Import students</h2>
          <p className={portal.subtitle}>Add many students at once from an Excel file. Nothing already in the system is changed.</p>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <Card title="1. Prepare your file">
        {guide.loading ? <LoadingSkeleton type="text" rows={5} /> : guide.error ? <div className="alert alert-error">{guide.error}</div> : (
          <>
            <ul className={styles.rules}>
              <li>Save the file as Excel <strong>.xlsx</strong>. Put the column names in the first row and one student in each row after it.</li>
              <li>Only the student's name is required. Leave any other cell empty if you don't have it.</li>
              <li>Column order does not matter, and extra columns are ignored.</li>
              <li>Up to {guide.data.max_rows.toLocaleString()} students and {guide.data.max_file_mb}MB per file.</li>
            </ul>
            <Table
              columns={[
                { header: 'Column', render: (row) => <strong>{row.column}</strong> },
                { header: '', render: (row) => <Badge variant={row.required === 'Required' ? 'primary' : 'default'}>{row.required}</Badge> },
                { header: 'What to put in it', accessor: 'rule' },
              ]}
              data={guide.data.columns}
            />
            <div className={styles.lists}>
              <div>
                <div className={styles.listTitle}>Classes you can use</div>
                {guide.data.classes.length > 0
                  ? <div className={styles.chips}>{guide.data.classes.map((name) => <Badge key={name} variant="info">{name}</Badge>)}</div>
                  : <p className={styles.note}>No classes exist yet. <Link to="/classes">Create the school's classes</Link> first, or import without a class column.</p>}
              </div>
              {guide.data.houses_enabled && (
                <div>
                  <div className={styles.listTitle}>Houses you can use</div>
                  {guide.data.houses.length > 0
                    ? <div className={styles.chips}>{guide.data.houses.map((name) => <Badge key={name} variant="info">{name}</Badge>)}</div>
                    : <p className={styles.note}>No houses exist yet. Leave the House column out, or <Link to="/houses">add houses</Link> first.</p>}
                </div>
              )}
            </div>
            <Button variant="secondary" size="sm" onClick={downloadTemplate}>
              <ArrowDownTrayIcon style={{ width: 16, height: 16 }} />
              Download template
            </Button>
          </>
        )}
      </Card>

      <Card title="2. Upload and check" className={styles.step}>
        <p className={styles.note}>Checking a file saves nothing. You will see which rows would be accepted and which rejected before anything is imported.</p>
        <div className={styles.uploadRow}>
          <input type="file" accept=".xlsx" onChange={chooseFile} aria-label="Excel file of students" />
          <Button variant="primary" onClick={() => submit(true)} disabled={!file || Boolean(busy)}>
            <ArrowUpTrayIcon style={{ width: 16, height: 16 }} />
            {busy === 'check' ? 'Checking…' : 'Check file'}
          </Button>
        </div>
      </Card>

      {report && (
        <Card title={imported ? '3. Import finished' : '3. Check result'} className={styles.step}>
          {imported ? (
            <div className="alert alert-success">
              {report.summary.imported} {report.summary.imported === 1 ? 'student was' : 'students were'} added from {report.filename}. <Link to="/students">View students</Link>
            </div>
          ) : (
            <p className={styles.note}>
              Read as {LAYOUT_TEXT.name[report.layout.name]} and {LAYOUT_TEXT.class[report.layout.class]}.
              {report.ignored_columns.length > 0 && ` Ignored columns: ${report.ignored_columns.join(', ')}.`}
            </p>
          )}

          <div className={portal.statsOnPage}>
            <div className={portal.stat}><div className={portal.statLabel}>Rows in file</div><div className={portal.statValue}>{report.summary.total}</div></div>
            <div className={portal.stat}><div className={portal.statLabel}>{imported ? 'Imported' : 'Will be imported'}</div><div className={`${portal.statValue} ${portal.statGood}`}>{report.summary.accepted}</div></div>
            <div className={portal.stat}><div className={portal.statLabel}>Rejected</div><div className={`${portal.statValue} ${report.summary.rejected > 0 ? portal.statBad : ''}`}>{report.summary.rejected}</div></div>
          </div>

          {!imported && (
            <div className={styles.uploadRow}>
              <Button variant="success" onClick={() => submit(false)} disabled={report.summary.accepted === 0 || Boolean(busy)}>
                {busy === 'import' ? 'Importing…' : `Import ${report.summary.accepted} ${report.summary.accepted === 1 ? 'student' : 'students'}`}
              </Button>
              {report.summary.rejected > 0 && report.summary.accepted > 0 && (
                <span className={styles.note}>The {report.summary.rejected} rejected {report.summary.rejected === 1 ? 'row is' : 'rows are'} skipped. Fix and upload them again later.</span>
              )}
            </div>
          )}

          {report.rejected.length > 0 && (
            <>
              <div className={styles.sectionHead}>
                <h3 className={portal.sectionTitle}>Rejected rows</h3>
                <Button variant="secondary" size="sm" onClick={downloadRejected}>
                  <ArrowDownTrayIcon style={{ width: 14, height: 14 }} />
                  Download rejected rows
                </Button>
              </div>
              <Table
                columns={[
                  ...studentColumns,
                  { header: 'Why it was rejected', render: (row) => <ul className={styles.reasons}>{row.reasons.map((reason) => <li key={reason}>{reason}</li>)}</ul> },
                ]}
                data={report.rejected}
              />
            </>
          )}

          {report.accepted.length > 0 && (
            <>
              <h3 className={portal.sectionTitle}>{imported ? 'Imported' : 'Accepted rows'}</h3>
              <Table
                columns={[
                  ...studentColumns,
                  { header: 'Date of birth', render: (row) => formatDate(row.date_of_birth) },
                  { header: 'National ID', render: (row) => row.national_id || '—' },
                  { header: 'Guardian', render: (row) => row.guardian_name || '—' },
                ]}
                data={report.accepted}
              />
            </>
          )}
        </Card>
      )}
    </div>
  );
}

export default StudentImport;
